<template>
  <div
    class="flex flex-col justify-between gap-1 h-full w-full
    text-EXPONDO-black relative overflow-hidden"
  >
    <CustomLink
      :url="link"
      :type="LINK_TYPE.NoStyle"
      @link-click="onClick"
    >
      <div
        class="flex flex-col gap-1"
      >
        <TwicpicsImage
          :src="image"
          :alt="imageAlt"
          :width="MEDIUM_CARD.IMAGE_WIDTH"
          :height="MEDIUM_CARD.IMAGE_HEIGHT"
        />
        <div class="h-7">
                      <ProductCardBadges
              v-if="showCardBadge"
              :tags="tags"
            />
        </div>
        <div class="h-6.5">
                      <ProductAverageReview
              v-if="rating"
              :rating="ratingToShow"
              :is-card-reviews="true"
            />
        </div>
        <div class="h-16">
          <p class="line-clamp-3 text-sm font-semibold">
            {{ title }}
          </p>
        </div>
      </div>
    </CustomLink>
    <EnergyRating
      v-if="energyRating"
      :energy-rating="energyRating"
      :size="ENERGY_RATING_SIZE.MEDIUM"
    />
    <CustomLink
      :url="link"
      :type="LINK_TYPE.NoStyle"
      @link-click="onClick"
    >
      <ProductCardPricing
        :full-product-price="fullProductPrice"
        :discounted-price="discountedPrice"
        :is-discounted="isDiscounted"
        :last30-days-price="last30DaysPrice"
        :size="PRODUCT_PRICING_SIZE.MEDIUM"
      />
    </CustomLink>
  </div>
</template>

<script lang="ts">

import { defineAsyncComponent } from 'vue';

import { LINK_TYPE } from '~~/types/components/Link/LinkType';
import type { ProductTag } from '~~/types/product/attribute/ProductTag';
import buildProductRating from '~~/helpers/product/buildProductRating';
import type { EnergyRatingDetails } from '~~/types/components/EnergyRating/EnergyRatingDetails';
import { MEDIUM_CARD } from '~~/constants/productCard';
import { ENERGY_RATING_SIZE } from '~~/constants/energyRating';
import { PRODUCT_PRICING_SIZE } from '~~/constants/productCardPricing';

import type { ProductCategories } from '~~/types/product/ProductCategories';
import { getFinalPriceFromAmounts } from '~~/helpers/product/getPriceInfo';

export default defineComponent({
  name: 'MediumProductCard',
  components: {
    
    ProductCardBadges: defineAsyncComponent(() => import('~/components/molecules/Product/ProductCardBadges.vue')),
    
    ProductAverageReview: defineAsyncComponent(() => import(/* webpackChunkName: "ProductAverageReview" */
      '~/components/atoms/Product/ProductAverageReview.vue')),
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    link: {
      type: String,
      required: true,
    },
    fullProductPrice: {
      type: Number,
      required: true,
    },
    discountedPrice: {
      type: Number,
      default: null,
    },
    isDiscounted: {
      type: Boolean,
      default: false,
    },
    last30DaysPrice: {
      type: Number,
      default: null,
    },
    sku: {
      type: String,
      required: true,
    },
    reviewsCount: {
      type: Number,
      default: 0,
    },
    rating: {
      type: Number,
      default: 0,
    },
    image: {
      type: String,
      default: '',
    },
    tags: {
      type: Array as PropType<ProductTag[]>,
      default: () => {

        return [];
      
      },
    },
    brand: {
      type: String,
      default: '',
    },
    energyRating: {
      type: Object as PropType<Maybe<EnergyRatingDetails>>,
      default: (): Maybe<EnergyRatingDetails> => null,
    },
    currency: {
      type: String,
      required: true,
    },
    categories: {
      type: [Object, String] as PropType<ProductCategories>,
      required: true,
    },
    productBoxTypeTag: {
      type: String,
      required: true,
    },
  },
  emits: ['product-clicked'],
  setup(props, { emit }) {

    const { trackClickProductBox } = useGtmEvents();
    const i18n = useI18n();

    const finalPrice = computed(() => getFinalPriceFromAmounts(props.discountedPrice, props.fullProductPrice));
    const showCardBadge = computed(() => props.tags?.length);
    const ratingToShow = computed(() => {

      return buildProductRating({
        averageRating: props.rating,
        count: props.reviewsCount,
      });
    
    });
    const imageAlt = computed(() => {

      return `${props.title} - ${i18n.t('Product Image')}`;
    
    });

    const onClick = () => {

      emit('product-clicked');
      trackClickProductBox({
        boxType: props.productBoxTypeTag,
        product: {
          sku: props.sku,
          name: props.title,
          currency: props.currency,
          discountedPrice: finalPrice.value,
          brand: props.brand,
          price: props.fullProductPrice,
          categories: props.categories,
        },
      });
    
    };

    return {
      LINK_TYPE,
      ratingToShow,
      showCardBadge,
      imageAlt,
      MEDIUM_CARD,
      ENERGY_RATING_SIZE,
      PRODUCT_PRICING_SIZE,
      onClick,
    };
  
  },
});

</script>
